<template>
  <div class="gift-reggie critical-component-clear">
    <transition
      mode="out-in"
      name="fade"
    >
      <div
        class="gift-reggie__default"
      >
        <!-- If Not Logged In -->
        <div v-if="!this.session" class="gift-reggie__copy">
          <h6>You must log in to add to your Wishlist</h6>
          <a href="https://www.dormify.com/account">Log In</a>
        </div>

        <!-- If Logged In -->
        <div v-if="this.session" class="gift-reggie__copy">
          <h6>Successfully added to Wishlist!</h6>
          <a href="https://www.dormify.com/pages/wishlist">View Wishlist</a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>


/**
 * Vue: Gift reggie (gift-reggie)
 * -----------------------------------------------------------------------------
 * A modal that appears when the user presses a heart icon to add an item to a wishlist or registry.
 *
 * @param {Boolean} isActive - Is overlay active?
 */

import { mapState, mapActions } from 'vuex'
import classes from '~/config/classes'
import timings from '~/config/timings'
import GiftReggieResponse from '~async/gift-reggie/gift-reggie-response'

export default {
  name: 'GiftReggie',
  components: { GiftReggieResponse },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    customer: {
      type: Object,
      default: () => ({
        id: '',
        email: '',
        grSecret: '',
      }),
    },
  },

  data() {
    return {
      action: '',
      loading: false,
      productId: '',
      reggieCookie: 'giftreggie_session_cookie',
      registryId: '',
      responseType: '',
      urlParams: null,
      retries: 3,
      session: null,
      variantId: '',
      wishlistId: '',
      wishlistVariants: [],
    }
  },

  computed: {

    /**
     * Map cart response.
     */
    ...mapState({
      cart: (state) => state.cart.response,
    }),

    /**
     * Expose class variables to use in template.
     * @returns {Object}
     */
    classes() {
      return classes
    },

    /**
     * Check cart attributes to see if the current variant already exists in
     * wishlist.
     * @returns {boolean}
     */
    existsInWishlist() {
      let wishlistIds = this.cart.attributes.wishlist_variant_ids
      wishlistIds = wishlistIds ? JSON.parse(wishlistIds) : []

      return wishlistIds.indexOf(this.variantId) > -1
    },

    /**
     * Compute the heart icon and its loading state.
     * @returns {SVGElement}
     */
    heartIcon() {
      return this.loading && this.action === 'wishlist' ?
        this.$icon('misc.loading') :
        this.$icon('misc.heart')
    },

    /**
     * Compute the gift icon and its loading state.
     * @returns {SVGElement}
     */
    giftIcon() {
      return this.loading && this.action === 'registry' ?
        this.$icon('misc.loading') :
        this.$icon('misc.giftRegistry')
    },
  },

  watch: {
    isActive() {
      if (!this.isActive) {
        this.resetModal()
      }
    },
  },

  mounted() {
    this.setLoginReturnParams()
    this.setEventBusListeners()
    this.initGiftReggie()
  },

  methods: {

    /**
     * Map cart actions
     */
    ...mapActions({
      updateCartAttributes: 'cart/updateCartAttributes',
      clearAttributes: 'cart/clearCartAttributes',
      openOverlay: 'overlays/open',
    }),

    /**
     * Add product to registry and display successful message.
     */
    addToRegistry() {
      this.loading = true
      if (!this.session) {
        this.delayedResetLoading()
        return
      }

      this.session.addRegistryProducts(
        this.registryId,
        this.productId,
        this.variantId,
        1,
        {},
      ).done(() => {
        this.loading = false
        this.responseType = 'success'
      }).fail((error) => {
        this.loading = false
        console.error('[GiftReggie - addToRegistry]', error)
      })
    },

    /**
     * Add variant to wishlist.
     * @returns {Promise<void>|undefined}
     */
    addToWishlist(button = null) {
      this.loading = true
      if (!this.session || !this.variantId) {
        this.delayedResetLoading()
        return
      }

      console.log('im running addToWishList');
      console.log('this.variantId -> ', this.variantId);

      this.session.addWishlist(this.variantId)
        .done(this.handleAddWishlist(), button ? button.classList.add('wishlist-active') : ()=>({}))
        .fail((res) => {
          const { status, response, responseText } = res
          const existsText = 'already have this item on your wishlist'

          if (status === 404) {
            this.session.openWishlist().done(this.addToWishlist)
          }
          else if (responseText?.includes(existsText) || response?.includes(existsText)) {
            this.responseType = 'remove'
            this.loading = false
          }
          else {
            console.error('[GiftReggie - addToWishlist]', res, res.response)
            this.loading = false
          }
        })
    },

    /**
     * Clear all related cart attributes.
     * @returns {Promise<void>}
     */
    async clearCartAttribute() {
      this.updateCartAttributes({
        attributes: {
          wishlist_variant_ids: '',
          wishlist_id: '',
        },
      })
      window.GiftReggie?.deleteCookie(this.reggieCookie)
    },

    /**
     * Reset loading after a delay for when a response is immediate.
     */
    delayedResetLoading() {
      setTimeout(() => this.loading = false, timings.l)
    },

    /**
     * Get the user's wishlist and update the cart attribute.
     */
    getWishlist() {
      if (!this.session) {
        return
      }
      this.session.getWishlist()
        .done(async (data) => {
          const { id, products } = data.wishlist
          this.wishlistId = id
          this.wishlistVariants = products.map(product => +product.variant_id)
          await this.updateCartAttribute()
          this.handleLoginReturn()
        })
        .fail((error) => {
          if (error.status !== 404) {
            console.error('[GiftReggie - getWishlist()]', error)

            if (this.retries <= 0) {
              console.error('[GiftReggie - getWishlist()] Too many initSession retries')
            }

            const { email, grSecret } = this.customer
            window.GiftReggie?.initSession(email, grSecret).done(newSession => {
              this.session = newSession
              this.getWishlist()
              cnvs.EventBus.emit('giftReggie:session', {
                session: this.session,
              })
            }).fail(error => {
              console.error('[GiftReggie - initSession]', error)
            })
            this.retries--
          }
          else {
            this.session.openWishlist().done(() => {
              this.handleLoginReturn()
            })
          }
        })
    },

    /**
     * Handle which action to perform based on which action is selected.
     */
    handleActionClick(action) {
      this.action = action

      if (!this.session) {
        this.responseType = 'login'
        return
      }

      if (this.action === 'wishlist') {
        if (!this.existsInWishlist) {
          this.addToWishlist()
        }
        else {
          this.responseType = 'remove'
        }
      }
      else {
        this.responseType = 'selectRegistry'
      }
    },

    /**
     * Handle successful wishlist add and display success message.
     */
    async handleAddWishlist() {
      this.wishlistVariants.push(this.variantId)
      await this.updateCartAttribute()
      this.loading = false
      this.responseType = 'success'
      this.gtmAddToWishlist()
    },

    gtmAddToWishlist() {
      const payload = {
        event: 'add_to_wishlist',
        currency : null,
        value : null,
        ecommerce: {
          items: [{
            //item_category: this.producttype,
            item_id : this.productId,
            //item_name : this.dataset.productTitle ,
            item_variant : this.variantId,
            //price : this.dataset.productPrice,
          }]
        }
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(payload);

    },

    /**
     * Handle modal interactions when the user returns after logging in.
     */
    handleLoginReturn() {
      const params = this.urlParams

      if (!params) {
        return
      }

      this.variantId = params.get('variant')
      this.productId = params.get('product')

      this.openOverlay({
        component: 'gift-reggie',
        ignoreDismissed: true,
        namespace: 'giftReggie',
      })

      this.handleActionClick(params.get('wishlist'))
    },

    /**
     * Handle registry selection from sub-component.
     * @param selectedRegistry
     */
    handleRegistryUpdate(selectedRegistry) {
      if (!selectedRegistry) {
        return
      }
      this.registryId = selectedRegistry
      this.addToRegistry()
    },

    /**
     * Update variant array and cart attributes when removed from wishlist.
     * @returns {Promise<void>}
     */
    async handleRemoveWishlist() {
      const index = this.wishlistVariants.indexOf(this.variantId)
      if (index > -1) {
        this.wishlistVariants.splice(index, 1)
        await this.updateCartAttribute()
      }
      this.loading = false
      this.responseType = ''
    },

    /**
     * Initialize gift reggie
     */
    initGiftReggie() {
      const { id, email } = this.customer

      const grSecret = this.customer.grSecret;

      if (!id || !email) {
        this.clearCartAttribute()
        cnvs.EventBus.emit(
          'giftReggie:session',
          { session: null },
        )
        return
      }

      if (window.GiftReggie) {
        window.GiftReggie.getSession(id, email, grSecret)
          .done(data => {
            if (window.gift_reggie) {
              window.gift_reggie.session = data
            }
            this.session = data
            this.getWishlist()
            cnvs.EventBus.emit('giftReggie:session', {
              session: this.session,
            })
          })
          .fail(error => console.error('[GiftReggie - getSession]', error))
      }
      else {
        console.error('Unable to load GiftReggie')
      }
    },

    /**
     * Remove variant from wishlist.
     */
    removeFromWishlist(button = null) {
      this.loading = true
      if (!this.session || !this.variantId) {
        this.loading = false
        return
      }

      this.session.removeWishlist(this.variantId)
        .done(this.handleRemoveWishlist(), button ? button.classList.remove('wishlist-active') : () => ({}))
        .fail(this.handleRemoveWishlist)
    },

    /**
     * Reset modal state. Have the wishlist button be selected by default
     * when the modal is opened.
     */
    resetModal() {
      this.action = ''
      this.responseType = ''
    },

    /**
     * Listen for an event to update IDs.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('giftReggie:update', this.updateIds)
      cnvs.EventBus.listen('giftReggie:sessionRequest', () => {
        cnvs.EventBus.emit('giftReggie:session', { session: this.session })
      })
    },

    /**
     * Set url params to use when the user returns from logging in.
     */
    setLoginReturnParams() {
      const params = new URLSearchParams(location.search)

      if (params.get('checkout_url')) {
        return
      }

      const required = [
        'variant', 'product', 'wishlist',
      ]
      const keys = [...params.keys()]
      const hasAllRequired = required.every(key => keys.indexOf(key) > -1)

      if (hasAllRequired) {
        this.urlParams = params

        // Clean up the URL and maintain unrelated params
        const newParams = new URLSearchParams(params)
        required.forEach(key => {
          newParams.delete(key)
        })
        history.replaceState({ html: '' }, '',
          `${location.href.replace(location.search, '')}?${newParams.toString()}`,
        )
      }
    },

    /**
     * Update cart attributes with wishlist data and variants.
     * @returns {Promise<*>}
     */
    async updateCartAttribute() {
      return await this.updateCartAttributes({
        attributes: {
          wishlist_variant_ids: this.wishlistVariants.map(variant => +variant),
          wishlist_id: this.wishlistId,
        },
      })
    },

    /**
     * Update the current variant and product in this modal component.
     * @param payload
     */
    updateIds(payload) {
      this.variantId = payload.variantId
      this.productId = payload.productId
      const button = payload.button || null;
      if (payload.action === 'add'){
        this.addToWishlist(button);
      } else {
        this.removeFromWishlist(button);
      }

    },
  },
}
</script>

<style lang="scss">
@import './gift-reggie';
@import './gift-reggie-overlay';
</style>
